(() => {
  const DEFAULT_DATA_URL = 'https://wpc-wildlife-live.jbmanning5.workers.dev/dashboard.json';
  // One documented freshness policy for the live station story.
  const FRESHNESS_MINUTES = { current: 15, delayed: 60 };
  const JOURNAL_FRESHNESS_MINUTES = { current: 90, delayed: 360 };
  const DETECTION_SOURCES = ['hearingNow', 'recentlyHeard', 'recentDetections', 'latestDetections', 'detections', 'todayDetections', 'todaySpecies'];
  // Matches wildlife-weather.js so the two modules accept the same public timestamps.
  const timestamp = value => {
    if (!value) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const raw = String(value).trim();
    const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}):(\d{2})$/);
    if (match) {
      const [, day, month, year, hour, minute] = match;
      return new Date(Number(year) < 100 ? 2000 + Number(year) : Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)).getTime();
    }
    const parsed = new Date(raw).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const getTime = item => timestamp(item?.lastHeardAt || item?.lastHeard || item?.last_heard || item?.last_heard_at || item?.detectedAt || item?.detected_at || item?.timestamp || item?.time || item?.date);
  const freshness = (value, thresholds = FRESHNESS_MINUTES) => {
    const valueTime = timestamp(value) || Number(value) || 0;
    if (!valueTime) return { state: 'unavailable', timestamp: 0 };
    const minutes = Math.max(0, (Date.now() - valueTime) / 60000);
    return { state: minutes <= thresholds.current ? 'current' : minutes <= thresholds.delayed ? 'delayed' : 'unavailable', timestamp: valueTime, minutes };
  };
  const first = (...values) => values.find(value => value !== undefined && value !== null && value !== '');
  const setText = (root, selector, value) => { const element = root.querySelector(selector); if (element) element.textContent = value; };
  const stateText = state => state === 'current' ? 'Current' : state === 'delayed' ? 'Delayed' : 'Unavailable';
  const exactTime = value => value ? new Date(value).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Not available';
  const age = result => !result?.timestamp ? 'not available' : result.minutes < 1 ? 'less than a minute ago' : result.minutes < 90 ? `${Math.round(result.minutes)} minutes ago` : `${Math.round(result.minutes / 60)} hours ago`;
  const nameOf = item => item?.commonName || item?.common_name || item?.species || item?.name || 'A recent species';
  const setStage = (root, name, state, title, detail) => {
    const stage = root.querySelector(`[data-about-stage="${name}"]`);
    if (!stage) return;
    stage.dataset.state = state;
    setText(stage, '[data-about-state]', title);
    setText(stage, '[data-about-detail]', detail);
  };

  function latestDetection(data) {
    return DETECTION_SOURCES.flatMap(key => Array.isArray(data?.[key]) ? data[key] : []).map(item => ({ item, time: getTime(item) })).sort((a, b) => b.time - a.time)[0] || { item: null, time: 0 };
  }

  function journalTimestamp(data) {
    return first(data?.generated_at, data?.generatedAt, data?.updated_at, data?.updatedAt, data?.journalUpdatedAt, data?.journal_updated_at);
  }

  function formatJournalHour(value) {
    const text = String(value ?? '').trim();
    const match = text.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (!match) return text;
    let hour = Number(match[1]);
    const minute = Number(match[2] || 0);
    const meridiem = match[3]?.toUpperCase();
    if (meridiem === 'PM' && hour < 12) hour += 12;
    if (meridiem === 'AM' && hour === 12) hour = 0;
    if (hour > 23 || minute > 59) return text;
    return new Date(2000, 0, 1, hour, minute).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  function journalDetail(data, result) {
    const completed = Array.isArray(data?.periods) ? data.periods.flatMap(period => Array.isArray(period?.completed_hours) ? period.completed_hours : (Array.isArray(period?.completedHours) ? period.completedHours : [])) : [];
    return completed.length ? `Published through ${formatJournalHour(completed.at(-1))}` : result.timestamp ? `Latest entry: ${new Date(result.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'The latest public journal update is unavailable';
  }

  function render(root, data, journalData = {}) {
    const detection = latestDetection(data);
    const detectionFreshness = freshness(detection.time);
    const weather = data?.weather || data?.weatherNow || data?.currentWeather || {};
    const weatherFreshness = freshness(first(weather.observedAt, weather.updatedAt, weather.timestamp, weather.time, weather.updatedAtRaw, data?.weatherUpdatedAt));
    const journalFreshness = freshness(journalTimestamp(journalData), JOURNAL_FRESHNESS_MINUTES);
    const publicFreshness = freshness(first(data?.updatedAt, data?.updated_at));
    const criticalStates = [detectionFreshness, detectionFreshness];
    const criticalUnavailable = criticalStates.some(item => item.state === 'unavailable');
    const criticalDelayed = criticalStates.some(item => item.state === 'delayed');
    const journalBehind = journalFreshness.state !== 'current';
    const overall = criticalUnavailable ? 'unavailable' : criticalDelayed || journalBehind ? 'delayed' : 'current';
    const overallCopy = criticalUnavailable
      ? ['The listening station is temporarily offline', 'Recent listening or wildlife identification data is unavailable. Please check back soon.']
      : criticalDelayed
        ? ['The listening station is catching up', 'Recent listening or wildlife identification data is delayed.']
        : journalBehind
          ? ['The listening station is active', 'Journal publication is delayed; listening and identification remain active.']
          : ['The listening station is active', 'Sound, wildlife detections, weather observations, and journal updates are arriving normally.'];
    const overallElement = root.querySelector('[data-about-overall]');
    if (overallElement) { overallElement.dataset.state = overall; setText(overallElement, 'strong', overallCopy[0]); setText(overallElement, 'span:last-child', overallCopy[1]); }

    setStage(root, 'listen', detectionFreshness.state, detectionFreshness.state === 'current' ? 'Listening' : detectionFreshness.state === 'delayed' ? 'Feed delayed' : 'Feed unavailable', detectionFreshness.timestamp ? `Recent sound received ${age(detectionFreshness)}` : 'Recent sound data is unavailable');
    setStage(root, 'identify', detectionFreshness.state, detectionFreshness.state === 'current' ? 'Identifying normally' : detectionFreshness.state === 'delayed' ? 'Observations delayed' : 'Observations unavailable', detection.time ? `Last heard: ${nameOf(detection.item)} · ${new Date(detection.time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : 'No recent wildlife observation is available');
    const temperature = first(weather.temperatureF, weather.temperature, weather.tempF, weather.temp);
    setStage(root, 'context', weatherFreshness.state, weatherFreshness.state === 'current' ? 'Weather current' : weatherFreshness.state === 'delayed' ? 'Weather delayed' : 'Weather unavailable', weatherFreshness.timestamp ? `${Number.isFinite(Number(temperature)) ? `${Number(temperature).toFixed(1)}°F · ` : ''}Updated ${age(weatherFreshness)}` : 'Current weather data is unavailable');
    setStage(root, 'share', journalFreshness.state, journalFreshness.state === 'current' ? 'Journal current' : journalFreshness.state === 'delayed' ? 'Journal publishing delayed' : 'Journal update unavailable', journalDetail(journalData, journalFreshness));

    const technical = root.querySelector('[data-about-technical]');
    if (technical) technical.innerHTML = [
      ['Microphone/feed', `${stateText(detectionFreshness.state)} · ${exactTime(detectionFreshness.timestamp)}`],
      ['Wildlife processing', `${stateText(detectionFreshness.state)} · ${detection.time ? exactTime(detection.time) : 'No recent detection'}`],
      ['Weather station', `${stateText(weatherFreshness.state)} · ${exactTime(weatherFreshness.timestamp)}`],
      ['Public data', `${stateText(publicFreshness.state)} · ${exactTime(publicFreshness.timestamp)}`],
      ['Journal update', `${stateText(journalFreshness.state)} · ${exactTime(journalFreshness.timestamp)}`]
    ].map(([label, value]) => `<div><dt>${label}</dt><dd>${value}</dd></div>`).join('');
  }

  async function init(root) {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    try {
      const url = root.dataset.dataUrl || DEFAULT_DATA_URL;
      const journalUrl = 'https://wpc-wildlife-live.jbmanning5.workers.dev/journal-latest.json';
      const stamp = `t=${Date.now()}`;
      const [dashboardResult, journalResult] = await Promise.allSettled([
        fetch(`${url}${url.includes('?') ? '&' : '?'}${stamp}`, { cache: 'no-store' }),
        fetch(`${journalUrl}?${stamp}`, { cache: 'no-store' })
      ]);
      const dashboard = dashboardResult.status === 'fulfilled' && dashboardResult.value.ok ? await dashboardResult.value.json() : {};
      const journal = journalResult.status === 'fulfilled' && journalResult.value.ok ? await journalResult.value.json() : {};
      render(root, dashboard, journal);
    } catch (_) { render(root, {}, {}); }
  }

  function start(scope = document) { scope.querySelectorAll?.('[data-wildlife-about-station]').forEach(init); }
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', () => start()) : start();
  document.addEventListener('shopify:section:load', event => start(event.target));
})();
